import { Card } from './Card';
import { Deck } from './Deck';
import { GameRules } from './GameRules';
import { CardColor, CardType } from '@/types/card';
import { GameState, GamePhase, SoloTeamChoice } from '@/types/game';
import { Player, Team } from '@/types/player';
import { GAME_CONFIG } from '@/utils/constants';

/**
 * playCard / drawCard 用のオプション
 */
export interface PlayCardOptions {
  /** スタッキングが許可されているか（ペナルティ重ねがけ判定用） */
  allowStacking?: boolean;
}

export interface DrawCardOptions {
  /** ドロー後にターンを進めるか（デフォルト: true） */
  advanceTurn?: boolean;
}

/**
 * ゲーム状態管理クラス
 * ゲーム全体の状態を管理し、アクションを処理
 */
export class GameStateManager {
  private state: GameState;
  private deck: Deck;
  private soloTeamChoice: SoloTeamChoice | undefined;

  constructor(playerNames: string[], soloTeamChoice?: SoloTeamChoice) {
    if (playerNames.length !== GAME_CONFIG.PLAYERS) {
      throw new Error(`プレイヤーは${GAME_CONFIG.PLAYERS}人必要です`);
    }

    this.deck = new Deck();
    this.soloTeamChoice = soloTeamChoice;
    this.state = this.initializeGame(playerNames);
  }

  /**
   * ゲームを初期化
   */
  private initializeGame(playerNames: string[]): GameState {
    // チーム割り当てを決定
    // soloTeamChoice === 'team1': Player 1 & 2 = Team 1, Player 3 = Team 2 (デフォルト)
    // soloTeamChoice === 'team2': Player 1 = Team 2 (ソロ), Player 2 & 3 = Team 1
    const player1Team = this.soloTeamChoice === 'team2' ? Team.TEAM_2 : Team.TEAM_1;
    const player2Team = this.soloTeamChoice === 'team2' ? Team.TEAM_1 : Team.TEAM_1;
    const player3Team = this.soloTeamChoice === 'team2' ? Team.TEAM_1 : Team.TEAM_2;

    // プレイヤー作成
    const players: Player[] = [
      {
        id: '1',
        name: playerNames[0],
        team: player1Team,
        hand: [],
        isAI: false,
        isConnected: true
      },
      {
        id: '2',
        name: playerNames[1],
        team: player2Team,
        hand: [],
        isAI: false,
        isConnected: true
      },
      {
        id: '3',
        name: playerNames[2],
        team: player3Team,
        hand: [],
        isAI: false,
        isConnected: true
      }
    ];

    // デッキをシャッフル
    this.deck.shuffle();

    // 初期手札を配布
    players.forEach(player => {
      player.hand = this.deck.draw(GAME_CONFIG.INITIAL_HAND_SIZE);
    });

    // 最初の捨て札カードをドロー（ワイルドカードは避ける）
    let firstCard: Card | undefined;
    do {
      const drawn = this.deck.draw(1);
      if (drawn.length === 0) {
        // デッキが空の場合は新しいデッキを作成
        this.deck = new Deck();
        this.deck.shuffle();
        continue;
      }
      firstCard = drawn[0];
    } while (!firstCard || firstCard.color === CardColor.WILD);

    // 2v1 変則ターン順ルール (issue #47): cops / crook を識別
    // チーム人数 1 のチームが crook、チーム人数 2 のチームが cops
    const team1Indices: number[] = [];
    const team2Indices: number[] = [];
    players.forEach((p, idx) => {
      if (p.team === Team.TEAM_1) team1Indices.push(idx);
      else team2Indices.push(idx);
    });
    const isTeam1Crook = team1Indices.length === 1;
    const crookIndex = isTeam1Crook ? team1Indices[0] : team2Indices[0];
    const copIndices = isTeam1Crook ? team2Indices : team1Indices;
    // lastCopIndex の初期値: COP1 (copIndices[0]) を入れることで
    // - SOLO mode の crook 開始時: 「他の cop = COP2」が選ばれ spec の
    //   '犯人 → 警察2 → 犯人 → 警察1' パターンに一致
    // - DUO mode の cop 開始時: 最初の cop が advanceTurn 内で
    //   lastCopIndex を上書きするので初期値は事実上無意味
    const initialLastCopIndex = copIndices[0];

    return {
      phase: GamePhase.PLAYING,
      players,
      currentPlayerIndex: 0,
      direction: 1,
      deck: this.deck.getAllCards(),
      discardPile: [firstCard],
      drawCount: 0,
      drawPenaltyType: null,
      winner: null,
      declaredColor: undefined,
      hasDrawnThisTurn: false,
      crookIndex,
      copIndices,
      lastCopIndex: initialLastCopIndex,
      alternationFlipped: false
    };
  }

  /**
   * カードをプレイ
   * @param playerId プレイヤーID
   * @param cardId カードID
   * @param declaredColor ワイルドカードの場合の宣言色
   * @param options プレイオプション（allowStackingでペナルティ重ねがけ可否）
   * @returns 成功ならtrue
   */
  playCard(
    playerId: string,
    cardId: string,
    declaredColor?: CardColor,
    options?: PlayCardOptions
  ): boolean {
    // ゲーム終了済みなら拒否
    if (this.state.phase === GamePhase.FINISHED) {
      return false;
    }

    // プレイヤー取得
    const player = this.state.players.find(p => p.id === playerId);
    if (!player) return false;

    // 現在のプレイヤーでなければ拒否
    if (this.getCurrentPlayer().id !== playerId) {
      return false;
    }

    // カード取得
    const cardIndex = player.hand.findIndex(c => c.id === cardId);
    if (cardIndex === -1) return false;

    const card = player.hand[cardIndex];
    const topCard = this.getTopCard();

    // カードプレイ可能か検証（ペナルティ状況を考慮）
    // optionsが指定されている場合はペナルティを考慮した検証を実施。
    // 指定されない場合は従来通りの検証（既存テスト互換）。
    if (options !== undefined) {
      const allowStacking = options.allowStacking ?? false;
      if (!GameRules.canPlayCardWithPenalty(
        card,
        topCard,
        this.state.declaredColor,
        this.state.drawPenaltyType,
        allowStacking
      )) {
        return false;
      }
    } else if (!GameRules.canPlayCard(card, topCard, this.state.declaredColor)) {
      return false;
    }

    // ワイルドカードで色が宣言されていなければ拒否
    if (GameRules.needsColorDeclaration(card) && !declaredColor) {
      return false;
    }

    // 手札からカードを削除
    player.hand.splice(cardIndex, 1);

    // 捨て札に追加
    this.state.discardPile.push(card);

    // ワイルドカードの色宣言を保存
    if (GameRules.needsColorDeclaration(card)) {
      this.state.declaredColor = declaredColor;
    } else {
      this.state.declaredColor = undefined;
    }

    // カード効果を適用
    this.applyCardEffects(card);

    // ターン進行に伴いドロー履歴をリセット
    this.state.hasDrawnThisTurn = false;

    // 勝利条件チェック
    this.state.winner = GameRules.checkWinCondition(this.state.players);
    if (this.state.winner) {
      this.state.phase = GamePhase.FINISHED;
    }

    // デッキ状態を更新
    this.state.deck = this.deck.getAllCards();

    return true;
  }

  /**
   * カード効果を適用
   */
  private applyCardEffects(card: Card): void {
    // 効果適用前のプレイヤー（カードを出した本人）を捕捉
    const playingPlayer = this.getCurrentPlayer();

    // Skip効果 (issue #48)
    //   警察ターン: advanceTurn() で犯人を skip → 末尾 advanceTurn() で他警察へ
    //   犯人ターン: advanceTurn() で警察を skip → 末尾 advanceTurn() で犯人自身へ
    // 末尾の `this.advanceTurn()` (applyCardEffects 最下行) と合わせて
    // 「sequence を 2 つ進める」と等価になる。
    if (GameRules.shouldSkipNextPlayer(card)) {
      this.advanceTurn();
    }
    // 旧変則ルール (issue #16) は新ターン順番ルール (#48) に統合済みのため除去
    // - 警察 Skip: 自動的に「犯人 skip → 他警察」
    // - 犯人 Skip: 自動的に「警察 skip → 犯人自身」
    void playingPlayer;

    // Reverse効果 (issue #49)
    // cop alternation flip フラグを toggle する永続フラグ。
    // direction も併せて反転 (UI の方向インジケータ等に使用されるため互換維持)
    if (GameRules.shouldReverseDirection(card)) {
      this.state.alternationFlipped = !this.state.alternationFlipped;
      this.state.direction *= -1 as 1 | -1;
    }

    // Draw効果
    const drawCount = GameRules.getDrawCount(card);
    if (drawCount > 0) {
      this.state.drawCount += drawCount;
      // ペナルティ種別を設定（回避判定用）
      if (card.type === CardType.DRAW_TWO) {
        this.state.drawPenaltyType = 'DRAW_TWO';
      } else if (card.type === CardType.WILD_DRAW_FOUR) {
        this.state.drawPenaltyType = 'WILD_DRAW_FOUR';
      }
    }

    // ターンを進める
    this.advanceTurn();
  }

  /**
   * カードをドロー
   * @param playerId プレイヤーID
   * @param options ドローオプション（advanceTurn=falseでターンを進めない）
   */
  drawCard(playerId: string, options?: DrawCardOptions): void {
    // ゲーム終了済みなら拒否
    if (this.state.phase === GamePhase.FINISHED) {
      return;
    }

    // プレイヤー取得
    const player = this.state.players.find(p => p.id === playerId);
    if (!player) return;

    // 現在のプレイヤーでなければ拒否
    if (this.getCurrentPlayer().id !== playerId) {
      return;
    }

    // ドロー枚数を決定
    const drawCount = this.state.drawCount > 0 ? this.state.drawCount : 1;
    // ペナルティドロー（強制的なdrawCount分のドロー）かどうか
    const isPenaltyDraw = this.state.drawCount > 0;

    // カードをドロー
    const drawnCards = this.drawFromDeck(drawCount);
    player.hand.push(...drawnCards);

    // ドローカウントをリセット
    this.state.drawCount = 0;
    this.state.drawPenaltyType = null;

    // 宣言色はクリアしない（次のプレイヤーのために維持）
    // this.state.declaredColor = undefined;

    // ターン進行制御
    // - options.advanceTurn が明示的に false の場合: ターンを進めない（任意ドロー）
    // - options.advanceTurn が明示的に true の場合: ターンを進める
    // - options が未指定の場合: 従来通りターンを進める（既存テスト互換）
    const advanceTurn = options?.advanceTurn ?? true;

    if (advanceTurn) {
      this.advanceTurn();
      this.state.hasDrawnThisTurn = false;
    } else {
      // ターンを進めない場合は、このターンで既にドローしたフラグを立てる。
      // ペナルティドローの場合はフラグを立てない（次のプレイ/パスに影響しない）
      if (!isPenaltyDraw) {
        this.state.hasDrawnThisTurn = true;
      }
    }

    // デッキ状態を更新
    this.state.deck = this.deck.getAllCards();
  }

  /**
   * ターンをパス（任意ドロー後にカードをプレイしない選択）
   * @param playerId プレイヤーID
   * @returns 成功ならtrue
   */
  passTurn(playerId: string): boolean {
    // ゲーム終了済みなら拒否
    if (this.state.phase === GamePhase.FINISHED) {
      return false;
    }

    // 現在のプレイヤーでなければ拒否
    if (this.getCurrentPlayer().id !== playerId) {
      return false;
    }

    // このターンでドローしていなければパスは認めない
    if (!this.state.hasDrawnThisTurn) {
      return false;
    }

    // ターンを進める
    this.advanceTurn();
    this.state.hasDrawnThisTurn = false;
    return true;
  }

  /**
   * デッキからカードをドロー
   */
  private drawFromDeck(count: number): Card[] {
    const cards: Card[] = [];

    for (let i = 0; i < count; i++) {
      // デッキが空なら捨て札を再シャッフル
      if (this.deck.getCount() === 0) {
        this.reshuffleDeck();
      }

      // まだデッキが空ならスキップ
      if (this.deck.getCount() === 0) {
        break;
      }

      const drawn = this.deck.draw(1);
      if (drawn.length > 0) {
        cards.push(drawn[0]);
      }
    }

    return cards;
  }

  /**
   * 捨て札をデッキに戻して再シャッフル
   */
  private reshuffleDeck(): void {
    // 捨て札の一番上のカードを残す
    if (this.state.discardPile.length <= 1) {
      return;
    }

    const topCard = this.state.discardPile.pop()!;
    const cardsToShuffle = [...this.state.discardPile];

    // デッキに戻してシャッフル
    this.deck.addCards(cardsToShuffle);
    this.deck.shuffle();

    // 捨て札を一番上のカードだけに
    this.state.discardPile = [topCard];
  }

  /**
   * ターンを進める (2v1 変則ルール、issue #47/#48/#49)
   *
   * 警察 → 犯人 → 警察 → 犯人 → ... の固定パターンで巡回する。
   * - 警察ターン後: 必ず犯人へ
   * - 犯人ターン後: alternationFlipped によって警察を選ぶ
   *   - false (通常): lastCopIndex とは違う警察 (＝交互)
   *   - true (Reverse 後): lastCopIndex と同じ警察 (＝同じ警察が連続)
   * alternationFlipped は Reverse カードのみが toggle する永続フラグ。
   */
  private advanceTurn(): void {
    const isCrook = this.state.currentPlayerIndex === this.state.crookIndex;

    if (!isCrook) {
      // 警察ターン終了 → 次は犯人。最後の警察を記録。
      this.state.lastCopIndex = this.state.currentPlayerIndex;
      this.state.currentPlayerIndex = this.state.crookIndex;
      return;
    }

    // 犯人ターン終了 → 次は警察
    if (this.state.alternationFlipped) {
      // 同じ警察に戻す
      this.state.currentPlayerIndex = this.state.lastCopIndex;
    } else {
      // 他の警察へ
      const otherCop = this.state.copIndices.find(
        (idx) => idx !== this.state.lastCopIndex
      );
      // copIndices は常に 2 要素なので必ず見つかるが、防御的に fallback
      this.state.currentPlayerIndex = otherCop ?? this.state.lastCopIndex;
    }
  }

  /**
   * ゲーム状態を取得
   */
  getState(): GameState {
    return { ...this.state };
  }

  /**
   * 捨て札の一番上のカードを取得
   */
  getTopCard(): Card {
    return this.state.discardPile[this.state.discardPile.length - 1];
  }

  /**
   * 現在のプレイヤーを取得
   */
  getCurrentPlayer(): Player {
    return this.state.players[this.state.currentPlayerIndex];
  }

  /**
   * プレイヤーの手札を取得
   */
  getPlayerHand(playerId: string): Card[] {
    const player = this.state.players.find(p => p.id === playerId);
    return player ? [...player.hand] : [];
  }

  /**
   * チームメイトの手札を取得
   */
  getTeammateHands(playerId: string): Card[][] {
    const player = this.state.players.find(p => p.id === playerId);
    if (!player) return [];

    const teammates = this.state.players.filter(
      p => p.team === player.team && p.id !== playerId
    );

    return teammates.map(t => [...t.hand]);
  }

  /**
   * ターン開始時の自動ペナルティドロー処理
   * Draw2/Draw4のペナルティを回避できない場合、自動でカードを引く
   * @param allowStacking スタッキングが許可されているか
   * @returns 自動ドローが実行されたか
   */
  processAutoPenaltyDraw(allowStacking: boolean): boolean {
    if (this.state.drawCount === 0) return false;

    const currentPlayer = this.getCurrentPlayer();
    if (GameRules.canAvoidDrawPenalty(
      currentPlayer.hand,
      this.state.drawPenaltyType,
      allowStacking
    )) {
      return false; // 回避可能、自動ドローしない
    }

    // 自動ドロー実行（drawCard()は既にdrawCount分引く設計）
    this.drawCard(currentPlayer.id);
    return true;
  }
}
